import { Alert, Autocomplete, Button, Chip, FormControl, Typography } from "@mui/material";
import { ChipWrap } from "components/ChipWrap";
import { findOption, optionValuesEqual, renderOptionWithHighlight, searchTextField, sortOptionsAlphabetically } from "utils/searchableOptions";
import { Box, Stack } from "@mui/system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AuthContextData, IsUnauthorizedError, useLogin } from "providers/Login";
import { useLocation, useBlocker } from "react-router";
import { useNotification } from "providers/Notification";
import { ConfirmDialog } from "components/ConfirmDialog";
import DataTable, { Column, RestoreColumnsState, SaveColumnsState, useTableColumnState } from "components/Table";
import StatusLabel from "components/StatusLabel";
import { Refresh } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import countries from "i18n-iso-countries";
import clientDevices from "../../../../constant/clientDevices";
import contentTypes from "../../../../constant/contentTypes";
import countriesEn from "i18n-iso-countries/langs/en.json";
import "flatpickr/dist/themes/airbnb.css";
import { FilterBar, FilterFieldData, FilterItem, SimpleFilterState } from "./filterbar";
import { ChipList } from "../../../../components/ChipList";
import { ActivityFilter, ActivityFilterField, FilterSelector } from "./filterSelector";
import { ColumnSelector } from "./columnSelector";
import { FilterTab } from "./filterTab";
import SearchIndexNotice from "../Services/_components/SearchIndexNotice";
import { formatDateTime, fromUnix } from "helpers/dateFormat";
import { useRole } from "hooks/useRole";

countries.registerLocale(countriesEn);

const BYTES_IN_KB = 1024;
const BYTES_IN_MB = 1048576;

interface ActivityListState {
    count: number
    items: ActivityListStateItem[]
}

interface ActivityListStateItem {
    id: string
    url: string
    icapMode: string
    contentType: string
    timestamp: Date
    clientIPAddress: string
    clientIPLocation: string
    clientUserAgent: string
    clientDevice: string
    originalValueStored: boolean
    obfuscated: boolean
    detected: boolean
    blocked: boolean
    scanned: boolean
    bypassed: boolean
    username: string
    userGroup: string
    instanceIdentifier: string
    apps: appInfo[]
    datatypesDetected: datatypeCount[]
    datatypesObfuscated: datatypeCount[]
    rules: ruleInfo[]
    httpMethod: string
    httpStatusCode: number
    bodyLength: number
    logMessage: string
}

interface appInfo {
    id: string
    name: string
    deleted: boolean
}

interface ruleInfo {
    id: string
    name: string
    deleted: boolean
}

interface datatypeCount {
    id: string
    name: string
    count: number
    isGroupDataType: boolean
    groupObfuscatedCount: number
    values: string[]
}

type columnsType = "id" | "timestamp" | "icapMode" | "instanceId" | "username" | "userGroup" | "apps_names" |
    "hostname" | "url" | "contentType" | "ip" | "device" | "rules_names" | "ip_location" |
    "datatypes_detected_names" | "datatypes_obfuscated_names" | "scanned" | "detected" | "obfuscated" | "blocked" | "bypassed" | "originalValueStored" | "originalValues" |
    "httpMethod" | "httpStatusCode" | "bodyLength" | "logMessage";


type SortDirection = "asc" | "desc";
type SortField = "contentType" | "device" | "scanned" | "detected" | "obfuscated" | "blocked" | "hostname" | "timestamp" | "instanceId" | "ip" | "username" | "userGroup" | "httpStatusCode" | "bodyLength";

interface FilterBarSortData {
    field: SortField
    order: SortDirection
}

const defaultSimpleQueryState: SimpleFilterState = {
    apps: [],
    clientDevices: [],
    contentTypes: [],
    originalValueStored: [],
    types: [],
    originalValues: [],
    detected: [],
    detectedDatatypes: [],
    hostnames: [],
    instancesIds: [],
    obfuscated: [],
    blocked: [],
    bypassed: [],
    obfuscatedDatatypes: [],
    usernames: [],
    userGroups: [],
    groups: [],
    rules: [],
    scanned: [],
    clientIpAddresses: [],
    location: [],
};

export default function Activites() {
    const notification = useNotification();
    const login = useLogin();
    const location = useLocation();
    // NS-753
    //every role can create a saved search, but only a policy role can change
    //or delete one
    const { canEditPolicy } = useRole();

    const [finishedLoading, setFinishedLoading] = useState(false);
    const [activityLogging, setActivityLogging] = useState(true);

    const [skipItems, setSkipItems] = useState(0);
    const [takeCount, setTakeCount] = useState(25);

    const [lastUpdated, setLastUpdated] = useState(new Date());

    const [activityFilters, setActivityFilters] = useState<ActivityFilter[]>([]);
    const [selectedFilter, setSelectedFilter] = useState<ActivityFilter | null>(null);

    const [filterData, setFilterData] = useState<FilterFieldData>();
    const [lastError, setLastError] = useState("");
    const [query, setQuery] = useState("");
    const [saveChangesDialog, setSaveChangesDialog] = useState(false);
    const [saveChangesCallback, setSaveChangesCallback] = useState<() => Promise<any>>();

    const [selectedOriginalValueDataType, setSelectedOriginalValueDataType] = useState("");

    const [tableDensity, setTableDensity] = useState("small" as "medium" | "small");

    const columnsDataId = "dataGrid_activityTable_columns";

    const defaultColumns: Column<columnsType>[] = useMemo(() => [
        { columnId: "timestamp", width: "auto", direction: "desc", sortOrder: 1 },
        { columnId: "icapMode", width: "auto" },
        { columnId: "apps_names", width: "auto" },
        { columnId: "hostname", width: "auto" },
        { columnId: "rules_names", width: "auto" },
        { columnId: "username", width: "auto" },
        { columnId: "userGroup", width: "auto" },
        { columnId: "datatypes_detected_names", width: "auto" },
        { columnId: "datatypes_obfuscated_names", width: "auto" },
        { columnId: "scanned", width: "auto" },
        { columnId: "detected", width: "auto" },
        { columnId: "obfuscated", width: "auto" },
    ], []);

    const [columnsState, setColumnsState] = useTableColumnState<columnsType>(defaultColumns);

    const [listState, setListState] = useState({ items: [], count: 0 } as ActivityListState);
	const loadGeneration = useRef(0);

    const [exportCsvLoading, setExportCsvLoading] = useState(false);

    let hasUnsavedChanges = false;

    if (finishedLoading && selectedFilter) {
        hasUnsavedChanges = selectedFilter.id.indexOf("temp") >= 0;
        hasUnsavedChanges = hasUnsavedChanges || selectedFilter.query !== query;
        hasUnsavedChanges = hasUnsavedChanges || selectedFilter.pageCount !== takeCount;
        hasUnsavedChanges = hasUnsavedChanges || selectedFilter.tableDensity !== tableDensity;
        hasUnsavedChanges = hasUnsavedChanges || (JSON.stringify(selectedFilter.fields) !== JSON.stringify(columnsState.map(x => {
            return {
                columnId: x.columnId,
                width: x.width
            };
        })));
    }

    const defaultFilter = {
        apps: [],
        clientDevices: [],
        clientIpAddresses: [],
        contentTypes: [],
        originalValueStored: [],
        originalValues: [],
        detected: [],
        types: [],
        detectedDatatypes: [],
        hostnames: [],
        instancesIds: [],
        obfuscated: [],
        blocked: [],
        bypassed: [],
        obfuscatedDatatypes: [],
        rules: [],
        scanned: [],
        usernames: [],
        userGroups: [],
        groups: [],
        location: [],
    };

    const [simpleQueryState, setSimpleQueryState] = useState<SimpleFilterState>(defaultFilter);
    const [queryBarLoading, setQueryBarLoading] = useState(false);

    const req = login.GetAxios();

    const blocker = useBlocker(data => {
        if (hasUnsavedChanges) {
            setSaveChangesDialog(true);
            setSaveChangesCallback(() => {
                return async () => {
                    if (blocker.proceed) {
                        blocker.proceed();
                    }

                    if (blocker.reset) {
                        blocker.reset();
                    }
                };
            });
            return true; // Block navigation
        }
        return false; // Allow navigation
    });

    useEffect(() => {
        if (!hasUnsavedChanges && blocker.reset) {
            blocker.reset();
        }
    }, [hasUnsavedChanges, blocker]);


    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = ""; // Required for Chrome to show the confirmation dialog
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);


        const loadData = useCallback(async (colState: typeof columnsState, query: string, itensPerPage: number, skip: number, generation = ++loadGeneration.current) => {
		const isCurrent = () => generation === loadGeneration.current;
        setFinishedLoading(false);
        setLastUpdated(new Date());
        setLastError("");

        if (!req) {
            return; //not logged in, nothing to do
        }
        try {
            const config = await req.get("/api/config");
			if (!isCurrent()) return;
            setActivityLogging(config.data.activity.enable);

            if (config.data.activity.enable) {
                const resp = await req.get("/api/activities", {
                    params: {
                        take: itensPerPage,
                        skip: skip,
                        search: query,
                    }
                });
				if (!isCurrent()) return;

                setListState({
                    count: resp.data.count,
                    items: (resp.data.items || []).map((x: any) => {
                        const item: ActivityListStateItem = {
                            ...x,
                            timestamp: fromUnix(x.timestamp),
                        };
                        return item;
                    })
                });

                const sortBy = resp.data.orderBy as FilterBarSortData[];
                const newState = colState.map(x => {
                    const idx = sortBy.findIndex(y => y.field === x.columnId);
                    if (idx === -1) {
                        return {
                            ...x,
                            sortOrder: undefined,
                            direction: undefined,
                        };
                    }

                    return {
                        ...x,
                        sortOrder: idx + 1,
                        direction: sortBy[idx].order
                    };
                });

                //if the column we are sorting by is not present, we must add it
                sortBy.forEach((x, idx) => {
                    const found = newState.find(y => y.columnId === x.field);
                    if (!found) {
                        newState.push({
                            columnId: x.field,
                            sortOrder: idx + 1,
                            direction: x.order,
                        });
                    }
                });


                setColumnsState(newState);
            }

            if (!isCurrent()) return;
            const filterFields = await FetchFilterFieldData(login);
            if (!isCurrent()) return;
            setFilterData(filterFields);

        } catch (err) {
			if (!isCurrent()) return;
            if (axios.isAxiosError(err) && err.response && !IsUnauthorizedError(err)) {
                setLastError(err.response.data.message);
            }
            console.error(err);
            notification.Display({
                title: "Error",
                message: "Could not fetch data",
                type: "error",
            });
            setListState({
                count: 0,
                items: [],
            });
        }
        if (isCurrent()) setFinishedLoading(true);
    }, [login, notification, req, setColumnsState]);

    const downloadCSV = async () => {
        if (!req) {
            notification.Display({
                title: "Error",
                message: "not logged in",
                type: "error",
            });
            return;
        }

        // An empty selection would send "columns=", which the server cannot tell
        // apart from the param being absent - and absent deliberately means
        // "export every column" for older callers. Exporting all 27 columns to a
        // user who has hidden every one of them is the opposite of what they
        // asked for, so stop here instead.
        if (columnsState.length === 0) {
            notification.Display({
                title: "Error",
                message: "select at least one column to export",
                type: "error",
            });
            return;
        }

        try {
            setExportCsvLoading(true);

            // URLSearchParams encodes each value - interpolating `query` raw used
            // to truncate the search at the first "&"/"#" inside a filter value
            // (URLs and free text routinely contain them), and an empty search
            // matches everything, which is why the export could return the whole
            // log instead of the filtered rows.
            const params = new URLSearchParams({
                search: query,
                originalValueField: selectedOriginalValueDataType,
                // columnsState is already the visible columns in on-screen order.
                columns: columnsState.map(c => c.columnId).join(","),
                token: login.GetToken() ?? "",
            });

            const link = document.createElement("a");
            link.href = `/api/activities/csv?${params.toString()}`;
            link.target = "_blank";

            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();

            document.body.removeChild(link);

        } catch (err) {
            console.error(err);
            notification.Display({
                title: "Error",
                message: "could not generate csv",
                type: "error",
            });
        } finally {
            setExportCsvLoading(false);
        }
    };

    const getFilterSorting = useCallback((c: typeof columnsState) => {
        return c.filter(x => x.sortOrder !== undefined).sort((a, b) => a.sortOrder! - b.sortOrder!).map(x => {
            return {
                field: x.columnId,
                order: x.direction!,
            } as FilterBarSortData;
        });
    }, []);

    const getQueryFromSimple = useCallback(async (x: SimpleFilterState, sort: typeof columnsState) => {
        const r = login.GetAxios();
        if (!r) {
            return;
        }


        const conversion = await r.post("/api/activities/convertsearch", {
            simpleToAdvanced: {
                ...x,
                orderBy: getFilterSorting(sort),
            }
        });

        return conversion.data.simpleToAdvanced || "";
    }, [getFilterSorting, login]);

    const getOriginalValues = (detectedDataTypes: datatypeCount[]): string[] => {
        let originalValues: string[] = [];

        if (!selectedOriginalValueDataType || !detectedDataTypes)
            return originalValues;

        detectedDataTypes
            .filter(x => x.id === selectedOriginalValueDataType)
            .forEach(x => x.values && originalValues.push(...x.values));

        return originalValues;
    };

    // generation ties the conversion to the load that asked for it. A caller
    // outside a load leaves it out and always commits; one inside a load
    // passes its own, so a conversion that outlives the load cannot replace
    // the simple state a newer one has already put on screen.
    const setSimpleQueryFromAdvanced = useCallback(async (advancedQuery: string, generation?: number) => {
        if (!req) {
            return false;
        }
        const isCurrent = () => generation === undefined || generation === loadGeneration.current;
        try {
            setQueryBarLoading(true);
            const conversion = await req.post("/api/activities/convertsearch", {
                advancedToSimple: advancedQuery,
            });
            if (!isCurrent()) return false;

            const newState = conversion.data as SimpleFilterState;
            setSimpleQueryState(newState);

            return true;
        } catch (err) {
            console.error(err);
            if (axios.isAxiosError(err) && err.response) {
                const body = err.response.data;

                notification.Display({
                    title: "Error",
                    message: body.message.charAt(0).toUpperCase() + body.message.slice(1),
                    type: "error",
                });
            } else {
                notification.Display({
                    title: "Error",
                    message: "Could not fetch data",
                    type: "error",
                });
            }
            if (isCurrent()) setSimpleQueryState(defaultSimpleQueryState);
        } finally {
            setQueryBarLoading(false);
        }

        return false;
    }, [notification, req]);

    const createFilter = useCallback(async () => {
        const len = activityFilters.filter(x => x.name.toLowerCase().indexOf("untitled") >= 0).length;
        const col = RestoreColumnsState("activity", defaultColumns);
        const filter = {
            id: `temp-${Date.now()}`,
            name: "Untitled",
            tableDensity: "small",
            order: activityFilters.length,
            pageCount: 25,
            query: await getQueryFromSimple(defaultSimpleQueryState, col),
            fields: col.map(x => {
                return {
                    columnId: x.columnId,
                    width: x.width || "auto",
                };
            }),
            enabled: 1,
        } as ActivityFilter;

        if (len) {
            filter.name = `Untitled ${len}`;
        }

        setActivityFilters(activityFilters.concat([filter]));
        setSelectedFilter(filter);
    }, [activityFilters, defaultColumns, getQueryFromSimple]);

    const restoreFilter = useCallback(async () => {
        if (selectedFilter) {
            setQuery(selectedFilter.query);
            setSimpleQueryFromAdvanced(selectedFilter.query);
            setTableDensity(selectedFilter.tableDensity as any);
            setTakeCount(selectedFilter.pageCount);
            loadData(selectedFilter.fields as Column<columnsType>[], selectedFilter.query, selectedFilter.pageCount, selectedFilter.tableDensity as any);
        }
    }, [loadData, selectedFilter, setSimpleQueryFromAdvanced]);

    const duplicateFilter = useCallback(async () => {
        const name = selectedFilter ? selectedFilter.name : "Main";

        const newFilter = {
            id: `temp${Date.now()}`,
            name: `${name} ${hasUnsavedChanges || !selectedFilter ? "(Changed)" : "(Copy)"}`,
            query,
            enabled: 1,
            tableDensity,
            pageCount: takeCount,
            order: activityFilters.length,
            fields: columnsState.map(x => {
                return {
                    columnId: x.columnId,
                    width: x.width,
                } as ActivityFilterField;
            })
        } as ActivityFilter;

        setActivityFilters(activityFilters.concat([newFilter]));
        setSelectedFilter(newFilter);
    }, [activityFilters, columnsState, hasUnsavedChanges, query, selectedFilter, tableDensity, takeCount]);


    const onSaveChangesResult = async (discart: boolean) => {
        setSaveChangesDialog(false);


        if (discart && saveChangesCallback) {
            if (selectedFilter && selectedFilter.id.indexOf("temp") >= 0) {
                removeFilter();
            }

            saveChangesCallback();
        }
    };

    const onCreateFilter = () => {
        if (hasUnsavedChanges) {
            setSaveChangesDialog(true);
            setSaveChangesCallback(() => {
                return async () => {
                    createFilter();
                };
            });

            return;
        }


        createFilter();
    };

    const changeSelectedFilter = async (filter: ActivityFilter | null) => {
        if (hasUnsavedChanges) {
            setSaveChangesDialog(true);
            setSaveChangesCallback(() => {
                return async () => {
                    setSelectedFilter(filter);
                };
            });

            return;
        }


        setSelectedFilter(filter);

        if (filter === null) {
            setColumnsState(RestoreColumnsState("activity", defaultColumns));
        }
    };

    const removeFilter = useCallback(async () => {
        if (!selectedFilter || !req) {
            return;
        }

        if (selectedFilter.id.indexOf("temp") === -1) {
            await req.delete(`/api/activityfilters/${selectedFilter.id}`);
        }

        let index = activityFilters.findIndex(x => x.id === selectedFilter.id);
        setActivityFilters(activityFilters.filter(x => x.id !== selectedFilter.id));

        if (index > 0) {
            setSelectedFilter(activityFilters[index - 1]);
        } else {
            setSelectedFilter(null);
            setColumnsState(RestoreColumnsState("activity", defaultColumns));
        }
    }, [activityFilters, defaultColumns, req, selectedFilter, setColumnsState]);

    const loadActivityFilters = useCallback(async (): Promise<ActivityFilter[]> => {
        if (!req) {
            return []; //not logged in, nothing to do
        }

        const resp = await req.get("/api/activityfilters", { params: { sortBy: "order" } });

        if (resp?.data?.items?.length) {
            const filters = resp?.data?.items as ActivityFilter[];
            setActivityFilters(filters);

            if (selectedFilter) {
                const selected = filters.find(x => x.id === selectedFilter?.id) || filters.find(x => x.name === selectedFilter.name);
                if (selected) {
                    setSelectedFilter(selected);
                }
            }
            return filters;
        }

        return [];
    }, [req, selectedFilter]);

    const saveFilter = useCallback(async (filter: ActivityFilter) => {
        if (!filter) {
            return;
        }

        if (selectedFilter && filter.id === selectedFilter.id) {
            filter.query = query;
            filter.pageCount = takeCount;
            filter.tableDensity = tableDensity || "small";
            filter.fields = columnsState.map(x => {
                return {
                    columnId: x.columnId,
                    width: x.width || "auto",
                };
            });
        }

        if (filter.id.indexOf("temp") >= 0) {
            filter.id = "";
            await req?.post("/api/activityfilters", filter);
        } else {
            await req?.put(`/api/activityfilters/${filter.id}`, filter);
        }
    }, [selectedFilter, query, takeCount, tableDensity, columnsState, req]);

    const saveSelectedFilter = useCallback(async (name?: string) => {
        if (!selectedFilter) {
            return;
        }

        const filter = activityFilters.find(x => x.id === selectedFilter.id);

        if (!filter) {
            return;
        }

        const index = activityFilters.indexOf(filter);

        if (index !== filter.order) {
            filter.order = index;
        }

        if (name) {
            filter.name = name;
        }

        await saveFilter(filter);

        await loadActivityFilters();
    }, [activityFilters, loadActivityFilters, saveFilter, selectedFilter]);

    const saveFilterSelection = async (filters: ActivityFilter[]) => {
        await Promise.all(filters.map((filter) => {
            return saveFilter(filter);
        }));

        await loadActivityFilters();
    };

    const firstLoad = useCallback(async () => {
		const generation = ++loadGeneration.current;
        const col = RestoreColumnsState("activity", defaultColumns);
        const advancedQuery = (new URLSearchParams(location.search)).get("query");
        const response = advancedQuery || await getQueryFromSimple(defaultSimpleQueryState, col);
		if (generation !== loadGeneration.current) return;
		await loadData(col, response, takeCount, skipItems, generation);
        // loadData discards its own results when superseded, but the query and
        // the columns are committed out here: without this the newer load's
        // rows would sit under this load's query bar and sort order.
        if (generation !== loadGeneration.current) return;
        setQuery(response);
        setColumnsState(col);

        if (response) {
            await setSimpleQueryFromAdvanced(response, generation);
        }
    }, [defaultColumns, getQueryFromSimple, loadData, location.search, setColumnsState, setSimpleQueryFromAdvanced, skipItems, takeCount]);

    useEffect(() => {
        firstLoad();
        loadActivityFilters();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (selectedFilter === null) {
            SaveColumnsState("activity", columnsState);
        }
    }, [columnsState, selectedFilter]);

    useEffect(() => {
        if (selectedFilter === null) {
            firstLoad();
        } else {
            setQuery(selectedFilter.query);
            setSimpleQueryFromAdvanced(selectedFilter.query);
            setTableDensity(selectedFilter.tableDensity as any);
            setTakeCount(selectedFilter.pageCount);
            loadData(selectedFilter.fields as Column<columnsType>[], selectedFilter.query, selectedFilter.pageCount, selectedFilter.tableDensity as any);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilter]);

    return <>
        <Box id="activity-page" display="flex" justifyContent="space-between">
            <Box>
                <Typography variant="h1" display="block">Data Log</Typography>
            </Box>
            <Box >
                <Typography variant="body2" color="text.secondary" marginBottom={"10px"}>
                    Last updated at {formatDateTime(lastUpdated)} <Button size="small" variant="text" onClick={() => loadData(columnsState, query, takeCount, skipItems)}><Refresh sx={{ fontSize: "12px", marginRight: "2px" }} />refresh</Button>
                </Typography>
                <Box display="flex" alignItems="center" justifyContent={"end"}>
                    {canEditPolicy && <FilterSelector onSave={saveFilterSelection} filters={activityFilters} />}
                    <LoadingButton
                        id="export-button"
                        loading={exportCsvLoading}
                        variant="outlined"
                        sx={{ marginLeft: "10px" }}
                        onClick={downloadCSV}
                    >
                        Export
                    </LoadingButton>
                </Box>

            </Box>

        </Box>

        <FilterTab
            hasUnsavedChanges={hasUnsavedChanges}
            selectedFilter={selectedFilter}
            onRestore={restoreFilter}
            onSelectFilter={changeSelectedFilter}
            onCreateFilter={onCreateFilter}
            onDuplicate={duplicateFilter}
            onRemove={removeFilter}
            onSave={saveSelectedFilter}
            onRename={saveSelectedFilter}
            canEditSaved={canEditPolicy}
            filters={activityFilters} />

        <Stack flexGrow={1} gap={1} direction={"row"} alignItems={"start"} marginRight={"10px"} flexWrap={"wrap"}>
            {filterData ?
                <FilterBar
                    query={query}
                    loading={queryBarLoading}
                    simpleFilterState={simpleQueryState}
                    data={filterData}
                    onSimpleQueryChange={async (x) => {
						const generation = ++loadGeneration.current;
                        const response = await getQueryFromSimple(x, columnsState);
						if (generation !== loadGeneration.current) return;
                        setQuery(response);
                        setSkipItems(0);
                        loadData(columnsState, response, takeCount, 0, generation);
                        setSimpleQueryState(x);
                    }}
                    onAdvancedQueryChange={(x) => { setQuery(x); }}
                    onAdvancedQuerySubmit={() => {
                        setSkipItems(0);
                        loadData(columnsState, query, takeCount, 0);
                        setSimpleQueryFromAdvanced(query);
                    }}
                    onAdvancedToSimpleChanged={() => setSimpleQueryFromAdvanced(query)}
                    onClearFilter={() => {
                        setQuery("");
                        setSkipItems(0);
                        setSimpleQueryState(defaultFilter);
                        loadData(columnsState, "", takeCount, 0);
                    }}
                /> : <Box flexGrow={1} />}
            <ColumnSelector
                onSave={(x, density) => {
                    setColumnsState(x);
                    setTableDensity(density);
                }}
                onReset={() => {
                    setTableDensity("small");
                    loadData(defaultColumns, query, takeCount, skipItems);
                }}
                tableDensity={tableDensity}
                currentState={columnsState}
                columns={[
                    { columnId: "id", displayName: "Id" },
                    { columnId: "icapMode", displayName: "Type" },
                    { columnId: "timestamp", displayName: "Timestamp" },
                    { columnId: "instanceId", displayName: "Shield Instance" },
                    { columnId: "username", displayName: "Username" },
                    { columnId: "userGroup", displayName: "User Group" },
                    { columnId: "apps_names", displayName: "Apps" },
                    { columnId: "hostname", displayName: "Origin" },
                    { columnId: "url", displayName: "URL" },
                    { columnId: "contentType", displayName: "Content Type" },
                    { columnId: "ip", displayName: "Client IP Address" },
                    { columnId: "ip_location", displayName: "Client IP Location" },
                    { columnId: "device", displayName: "Client Device" },
                    { columnId: "rules_names", displayName: "Rule" },
                    { columnId: "datatypes_detected_names", displayName: "Detected Data Types" },
                    { columnId: "datatypes_obfuscated_names", displayName: "Obfuscated Data Types" },
                    { columnId: "originalValues", displayName: "Data Values" },
                    { columnId: "bypassed", displayName: "Bypassed" },
                    { columnId: "scanned", displayName: "Scanned" },
                    { columnId: "detected", displayName: "Detected" },
                    { columnId: "obfuscated", displayName: "Obfuscated" },
                    { columnId: "blocked", displayName: "Blocked" },
                    { columnId: "originalValueStored", displayName: "Values Stored" },
                    { columnId: "httpMethod", displayName: "HTTP Method" },
                    { columnId: "httpStatusCode", displayName: "HTTP Status" },
                    { columnId: "bodyLength", displayName: "Body Length" },
                    { columnId: "logMessage", displayName: "Log Message" },
                ]}
            />


        </Stack>

        {
            !activityLogging && <Alert severity="warning" sx={{ width: "100%", marginBottom: "10px", marginTop: "10px" }}>Activity logging is disabled</Alert>
        }
        <SearchIndexNotice
            stream="activities"
            onIndexChange={() => loadData(columnsState, query, takeCount, skipItems)}
        />
        {
            lastError && <Alert severity="error" sx={{ width: "100%", marginBottom: "10px", marginTop: "10px", whiteSpace: "pre-line" }}>{lastError}</Alert>
        }
        {
            lastError && <Alert severity="warning" sx={{ width: "100%", marginBottom: "10px", marginTop: "10px" }}>New activity records are being saved locally and will sync automatically once the connection is restored.</Alert>
        }


        <DataTable
            sx={{
                maxHeight: "90%",
            }}
            tableSx={{
                width: "max-content"
            }}
            size={tableDensity}
            dataCount={listState.count}
            paginationStartItem={skipItems}
            paginationItemCount={takeCount}
            onPaginationStartItemChanged={(x) => {
                setSkipItems(x);
                setTakeCount(takeCount);
                loadData(columnsState, query, takeCount, x);
            }}
            onPaginationItemCountChanged={(x) => {
                setTakeCount(Number(x));
                loadData(columnsState, query, Number(x), skipItems);
            }}
            data={listState.items}
            columnsState={columnsState}
            finishedLoading={finishedLoading}
            onSortChanged={async (c) => {
				const generation = ++loadGeneration.current;
                const r = login.GetAxios();
                if (!r) {
                    return;
                }

                const conversion = await r.post("/api/activities/convertsearch", {
                    modifyAdvanced: {
                        query: query,
                        orderBy: getFilterSorting(c),
                    }
                });
				if (generation !== loadGeneration.current) return;

                const response = conversion.data.modifyAdvanced || "";

                loadData(columnsState, response, takeCount, skipItems, generation);
                setQuery(response);
            }}
            onColumnOrderChanged={(x) => setColumnsState(x)}
            columnsDataId={columnsDataId}
            columns={{
                id: {
                    displayName: "Id",
                    render: (x) => {
                        return <Typography fontSize="small" title={x.id} maxWidth={"280px"} whiteSpace={"nowrap"} overflow={"hidden"} textOverflow="ellipsis">{x.id || "-"}</Typography>;
                    },
                },
                icapMode: {
                    displayName: "Type",
                    sortable: true,
                    render: (x) => {
                        switch (x.icapMode) {
                        case "REQMOD":
                            return "Icap Request";
                        case "RESPMOD":
                            return "Icap Response";
                        case "API":
                            return "API";
                        }

                        return "";
                    }
                },
                timestamp: {
                    displayName: "Timestamp",
                    sortable: true,
                    render: (x) => {
                        return <Typography fontSize="small">{formatDateTime(x.timestamp)}</Typography>;
                    },
                },
                instanceId: {
                    displayName: "Shield Instance",
                    sortable: true,
                    render: (x) => {
                        return x.instanceIdentifier;
                    }
                },
                username: {
                    displayName: "Username",
                    sortable: true,
                    render: (x) => {
                        return x.username;
                    }
                },
                userGroup: {
                    displayName: "User Group",
                    render: (x) => {
                        return <ChipWrap>
                            {
                                x.userGroup.split(",").filter(x => x !== "").map((x, i) => <Chip key={i} label={x.trim().replaceAll(/@.*/g, "")} variant="outlined" size="small" color="primary" />)
                            }
                        </ChipWrap>;
                    }
                },
                apps_names: {
                    displayName: "Apps",
                    maxWidth: "250px",
                    render: (x) => {
                        return <ChipWrap>

                            {x.apps.length > 0
                                ? (x.apps.map(y => <Chip key={y.id} label={y.name.trim()} variant="outlined" size="small" color={y.id.trim() === "no match" ? "error" : "primary"} sx={{ textDecoration: y.deleted ? "line-through" : undefined }} />))
                                : <Chip label="(Unknown)" variant="outlined" size="small" color="error" />
                            }

                        </ChipWrap>;
                    },
                },
                hostname: {
                    displayName: "Origin",
                    sortable: true,
                    render: (x) => {
                        let data = "";
                        try {
                            data = (new URL(x.url)).hostname;
                        } catch {
                            data = x.url;
                        }

                        return <Typography fontSize="small" title={x.url} maxWidth={"360px"} whiteSpace={"nowrap"} overflow={"hidden"} textOverflow="ellipsis">{data}</Typography>;
                    },
                },
                url: {
                    displayName: "URL",
                    render: (x) => {
                        return <Typography fontSize="small" title={x.url} maxWidth={"360px"} whiteSpace={"nowrap"} overflow={"hidden"} textOverflow="ellipsis">{x.url || "-"}</Typography>;
                    },
                },
                contentType: {
                    displayName: "Content Type",
                    sortable: true,
                    render: (x) => {
                        return x.contentType;
                    },
                },
                ip: {
                    displayName: "Client IP Address",
                    sortable: true,
                    render: (x) => {
                        return x.clientIPAddress;
                    },
                },
                ip_location: {
                    displayName: "Client IP Location",
                    render: (x) => {
                        if (x.clientIPLocation !== "Unknown") {
                            const a = countries.getName(x.clientIPLocation, "en");
                            return a;
                        }
                        return x.clientIPLocation;
                    },
                },
                device: {
                    displayName: "Client Device",
                    sortable: true,
                    render: (x) => {
                        return x.clientDevice;
                    },
                },
                rules_names: {
                    displayName: "Rule",
                    maxWidth: "250px",
                    render: (x) => {
                        return <ChipWrap>
                            {
                                x.rules.length > 0
                                    ? (x.rules.map(y => <Chip key={y.id} label={y.name.trim()} variant="outlined" size="small" color={y.id === "no match" ? "error" : "primary"} sx={{ textDecoration: y.deleted ? "line-through" : undefined }} />))
                                    : <Chip label="(None)" variant="outlined" size="small" color="error" />
                            }
                        </ChipWrap>;
                    },
                },
                originalValues: {
                    displayName: "Data Values",
                    renderSubHeader: () => {
                        const dataTypeOptions = sortOptionsAlphabetically((filterData?.detectedDataType || []).map(x => ({ value: x.id, label: x.label })));
                        return <FormControl sx={{ marginTop: "10px" }} variant="outlined" fullWidth size="small">
                            <Autocomplete
                                fullWidth
                                disableClearable={true as boolean}
                                id="data-type-select"
                                options={dataTypeOptions}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={optionValuesEqual}
                                value={findOption(dataTypeOptions, selectedOriginalValueDataType || null)}
                                onChange={(_, option) => setSelectedOriginalValueDataType(option?.value || "")}
                                renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(dataTypeOptions, selectedOriginalValueDataType || null)?.label)}
                                renderInput={(params) => searchTextField(params, { label: "Data Type *" })}
                            />
                        </FormControl>;
                    },
                    maxWidth: "250px",
                    render: (x) => {
                        return <ChipWrap>
                            <ChipList items={getOriginalValues(x.datatypesDetected)} renderItem={(x) => <Chip label={x} title={x} variant="outlined" size="small" color="primary" />} />
                        </ChipWrap>;
                    },
                },
                datatypes_detected_names: {
                    displayName: "Detected Data Types",
                    maxWidth: "250px",
                    render: (x) => {
                        return <ChipWrap>
                            <ChipList sort={(a, b) => a.name.localeCompare(b.name)} items={x.datatypesDetected} renderItem={(y) => <DataTypeChip id={y.id} name={y.name} count={y.count} isGroupDataType={y.isGroupDataType} groupObfuscatedCount={y.groupObfuscatedCount} values={y.values} key={y.id} />} />
                        </ChipWrap>;
                    },
                },
                datatypes_obfuscated_names: {
                    displayName: "Obfuscated Data Types",
                    maxWidth: "250px",
                    render: (x) => {
                        return <ChipWrap>
                            <ChipList sort={(a, b) => a.name.localeCompare(b.name)} items={x.datatypesObfuscated} renderItem={(y) => <DataTypeChip id={y.id} name={y.name} count={y.isGroupDataType ? y.groupObfuscatedCount : y.count} isGroupDataType={y.isGroupDataType} groupObfuscatedCount={y.groupObfuscatedCount} values={y.values} key={y.id} />} />
                        </ChipWrap>;
                    },
                },
                scanned: {
                    displayName: "Scanned",
                    sortable: true,
                    render: (x) => {
                        return x.scanned
                            ? <StatusLabel label="Yes" color="success" />
                            : <StatusLabel label="No" color="error" />;
                    },
                },
                originalValueStored: {
                    displayName: "Values Stored",
                    sortable: true,
                    render: (x) => {
                        return x.originalValueStored
                            ? <StatusLabel label="Yes" color="success" />
                            : <StatusLabel label="No" color="error" />;
                    },
                },
                detected: {
                    displayName: "Detected",
                    sortable: true,
                    render: (x) => {
                        return x.detected
                            ? <StatusLabel label="Yes" color="success" />
                            : <StatusLabel label="No" color="error" />;
                    },
                },
                obfuscated: {
                    displayName: "Obfuscated",
                    sortable: true,
                    render: (x) => {
                        return x.obfuscated
                            ? <StatusLabel label="Yes" color="success" />
                            : <StatusLabel label="No" color="error" />;
                    },
                },
                blocked: {
                    displayName: "Blocked",
                    sortable: true,
                    render: (x) => {
                        return x.blocked
                            ? <StatusLabel label="Yes" color="success" />
                            : <StatusLabel label="No" color="error" />;
                    },
                },
                bypassed: {
                    displayName: "Bypassed",
                    sortable: true,
                    render: (x) => {
                        return x.bypassed
                            ? <StatusLabel label="Yes" color="success" />
                            : <StatusLabel label="No" color="error" />;
                    },
                },
                httpMethod: {
                    displayName: "HTTP Method",
                    render: (x) => x.httpMethod || "-",
                },
                httpStatusCode: {
                    displayName: "HTTP Status",
                    sortable: true,
                    render: (x) => x.httpStatusCode === 0 ? "-" : x.httpStatusCode,
                },
                bodyLength: {
                    displayName: "Body Length",
                    sortable: true,
                    render: (x) => {
                        if (!x.httpMethod && x.bodyLength === 0) return "-";
                        if (x.bodyLength < 0) return "Chunked";
                        if (x.bodyLength === 0) return "Empty";
                        if (x.bodyLength < BYTES_IN_KB) return `${x.bodyLength} B`;
                        if (x.bodyLength < BYTES_IN_MB) return `${(x.bodyLength / BYTES_IN_KB).toFixed(1)} KB (${x.bodyLength})`;
                        return `${(x.bodyLength / BYTES_IN_MB).toFixed(1)} MB (${x.bodyLength})`;
                    },
                },
                logMessage: {
                    displayName: "Log Message",
                    render: (x) => (
                        <Typography fontSize="small" maxWidth="300px" noWrap title={x.logMessage}>
                            {x.logMessage || "-"}
                        </Typography>
                    ),
                },
            }}
        />

        <ConfirmDialog
            open={saveChangesDialog}
            title="Save Filter Changes"
            message="Unsaved changes detected. Do you want to discard the changes?"
            confirmLabel="Discard"
            confirmColor="error"
            onConfirm={() => onSaveChangesResult(true)}
            onCancel={() => onSaveChangesResult(false)}
        />
    </>;
}

function DataTypeChip(props: datatypeCount) {
    const fontWeight = props.values && props.values.some(x => x.length > 0) ? "bold" : "normal";
    return <Chip label={`${props.name.trim()} (${props.count})`} variant="outlined" size="small" sx={{ fontWeight }} color={props.isGroupDataType ? "success" : "primary"} />;
}


async function FetchFilterFieldData(login: AuthContextData): Promise<FilterFieldData> {
    const req = login.GetAxios();

    if (!req) {
        throw new Error("could not make request");
    }

    const appsResp = await req.get("/api/appsdeleted", {
        params: {
            sortBy: "name",
            sortDirection: "asc"
        }
    });


    const datatypesResp = await req.get("/api/datatypes");

    const rulesResp = await req.get("/api/rulesdeleted");

    // NS-747: the group list is admin-only and the Activity page is not. Without
    // it the Group filter has no values, but the page must still load.
    const groupsResp = await req.get("/api/user/groups").catch(() => ({ data: { groups: [] } }));

    const datatypes = ((datatypesResp.data.items || []).map((x: any) => { return { id: x.type, label: x.name }; }) as FilterItem[]).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));

    return {
        contentTypes: contentTypes.map(x => { return { id: x, label: x, deleted: false }; }),
        usernames: [],
        userGroups: [],
        groups: ((groupsResp.data.groups || []).map((x: any) => {
            return { id: x.id, label: x.directoryName ? `${x.identifier} (${x.directoryName})` : x.identifier, deleted: false };
        }) as FilterItem[]).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" })),
        clientDevice: clientDevices.map(x => { return { id: x, label: x, deleted: false }; }),
        apps: [{ id: "no match", label: "(Unknown)", deleted: false }].concat((appsResp.data || []).map((x: any) => { return { id: x.id, label: x.name, deleted: x.deleted }; }).sort((a: FilterItem, b: FilterItem) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }))),
        detectedDataType: datatypes,
        obfuscatedDataType: datatypes,
        rules: [{ id: "no match", label: "(None)", deleted: false }].concat((rulesResp.data || []).map((x: any) => { return { id: x.id, label: x.name, deleted: x.deleted }; }).sort((a: FilterItem, b: FilterItem) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }))),
        originalValueStored: [{ id: "true", label: "Yes", deleted: false }, { id: "false", label: "No", deleted: false }],
        types: [{ id: "api", label: "API", deleted: false }, { id: "reqmod", label: "Icap Request", deleted: false }, { id: "respmod", label: "Icap Response", deleted: false }],
        detected: [{ id: "true", label: "Datatypes Detected", deleted: false }, { id: "false", label: "Datatypes Not Detected", deleted: false }],
        obfuscated: [{ id: "true", label: "Datatypes Obfuscated", deleted: false }, { id: "false", label: "Datatypes Not Obfuscated", deleted: false }],
        bypassed: [{ id: "true", label: "Traffic Bypassed", deleted: false }, { id: "false", label: "Traffic Not Bypassed", deleted: false }],
        blocked: [{ id: "true", label: "Traffic Blocked", deleted: false }, { id: "false", label: "Traffic Not Blocked", deleted: false }],
        scanned: [{ id: "true", label: "Datatypes Scanned", deleted: false }, { id: "false", label: "Datatypes Not Scanned", deleted: false }],
        countries: Object.entries(countries.getAlpha2Codes()).map(([code]) => {
            return {
                id: code,
                label: countries.getName(code, "en") || "",
                deleted: false,
            };
        })
    };
}
